import React, { useState, useEffect, useRef } from "react";
import apiClient from "../api/ResturantClient";
import { subscribeToNewOrder, disconnectSocket } from "./utils/socket";
import OrderTicket from "./OrderTicket";

function OrderManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [addMenuItemOrder, setAddMenuItemOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const restaurantId = sessionStorage.getItem("restaurant_id");
  const [confirmDeleteOrderId, setConfirmDeleteOrderId] = useState(null);
  const [modifiedOrders, setModifiedOrders] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const isFetching = useRef(false);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [printingOrder, setPrintingOrder] = useState(null);

  const handlePrintTicket = (order) => {
    setPrintingOrder(order);
  };

  useEffect(() => {
    if (printingOrder) {
      const timer = setTimeout(() => window.print(), 50);
      return () => clearTimeout(timer);
    }
  }, [printingOrder]);

  useEffect(() => {
    if (restaurantId) {
      fetchOrders();
      subscribeToNewOrder(restaurantId, handleOrderUpdate);
    }
    return () => {
      disconnectSocket();
    };
  }, []); // Empty dependency

  const fetchOrders = async () => {
    if (isFetching.current) return;

    try {
      isFetching.current = true;
      setLoading(true);

      const response = await apiClient.get(
        `/owner/restaurant/${restaurantId}/orders?page=${currentPage}&per_page=${perPage}`
      );

      setOrders(response.data.orders);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      setError("Failed to load orders. Please try again later.");
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  const handleOrderUpdate = (orderData) => {
    orderData = JSON.parse(orderData);
    const isNewOrder = orders.find(
      (order) => order.order_id === orderData.order_id
    );
    if (!isNewOrder) {
      setOrders((prevOrders) => [orderData, ...prevOrders]);
    }
  };

  const toggleOrderItems = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleApproveOrder = (orderId) => {
    if (modifiedOrders[orderId]) {
      handleUpdateOrder(orderId);
    } else {
      approveOrder(orderId);
    }
  };

  const approveOrder = async (orderId) => {
    try {
      setIsUpdating((prev) => ({ ...prev, [orderId]: true }));
      const response = await apiClient.post(
        `/owner/restaurant/${restaurantId}/approve-order/${orderId}`
      );
      if (response.status === 200) {
        fetchOrders();
      }
    } catch (error) {
      console.error("Error approving order:", error);
    } finally {
      setIsUpdating((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const [isUpdating, setIsUpdating] = useState({});

  const handleUpdateOrder = async (orderId) => {
    if (isUpdating[orderId]) return;

    try {
      setIsUpdating((prev) => ({ ...prev, [orderId]: true }));
      const modifiedOrder = modifiedOrders[orderId];
      const response = await apiClient.put(
        `/customer/update-order/${orderId}`,
        {
          order_items: modifiedOrder.items,
          total_amount: calculateOrderTotal(modifiedOrder.items),
        }
      );

      if (response.status === 200) {
        fetchOrders();
        setModifiedOrders((prev) => {
          const updated = { ...prev };
          delete updated[orderId];
          return updated;
        });
      }
    } catch (error) {
      console.error("Error updating order:", error);
    } finally {
      setIsUpdating((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const calculateOrderTotal = (items) => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const updateOrderItemQuantity = (orderId, itemId, newQuantity) => {
    setOrders((prevOrders) => {
      const updatedOrders = prevOrders.map((order) => {
        if (order.order_id === orderId) {
          const updatedItems = order.items.map((item) =>
            item.menu_item_id === itemId
              ? { ...item, quantity: newQuantity }
              : item
          );

          const newTotal = calculateOrderTotal(updatedItems);
          const modifiedOrder = {
            ...order,
            items: updatedItems,
            total_amount: newTotal,
          };

          setModifiedOrders((prev) => ({
            ...prev,
            [orderId]: modifiedOrder,
          }));

          return modifiedOrder;
        }
        return order;
      });
      return updatedOrders;
    });
  };

  const handleIncrementQuantity = (orderId, item) => {
    const newQuantity = item.quantity + 1;
    updateOrderItemQuantity(orderId, item.menu_item_id, newQuantity);
  };

  const handleDecrementQuantity = (orderId, item) => {
    if (item.quantity > 1) {
      const newQuantity = item.quantity - 1;
      updateOrderItemQuantity(orderId, item.menu_item_id, newQuantity);
    }
  };

  const handleGoBack = () => {
    setAddMenuItemOrder(null);
    setSearchTerm("");
  };

  const handleDeleteOrderItem = (orderId, itemId) => {
    setOrders((prevOrders) => {
      const updatedOrders = prevOrders.map((order) => {
        if (order.order_id === orderId) {
          const updatedItems = order.items.filter(
            (item) => item.menu_item_id !== itemId
          );
          return {
            ...order,
            items: updatedItems,
            total_amount: calculateOrderTotal(updatedItems),
          };
        }
        return order;
      });

      setModifiedOrders((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          items: updatedOrders.find((order) => order.order_id === orderId)
            .items,
        },
      }));

      return updatedOrders;
    });
  };

  const getMenuItems = async (order) => {
    setAddMenuItemOrder(order);
    try {
      const response = await apiClient.get(
        `/menu/get-menu-items/${restaurantId}`
      );
      const allMenuItems = response.data;
      const existingItemIds = order.items.map((item) => item.menu_item_id);
      const filteredMenuItems = allMenuItems.filter(
        (item) => !existingItemIds.includes(item.menu_id)
      );
      setMenuItems(filteredMenuItems);
    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
  };

  const handleDeleteOrder = (orderId) => {
    setConfirmDeleteOrderId(orderId);
  };

  const addMenuItemToOrder = (menuItem) => {
    const order = addMenuItemOrder;
    const updatedItems = [
      ...order.items,
      {
        menu_item_id: menuItem.menu_id,
        quantity: 1,
        price: menuItem.price,
        product_name: menuItem.item_name,
      },
    ];
    const newTotal = calculateOrderTotal(updatedItems);

    setOrders((prevOrders) =>
      prevOrders.map((o) =>
        o.order_id === order.order_id
          ? { ...o, items: updatedItems, total_amount: newTotal }
          : o
      )
    );

    setModifiedOrders((prev) => ({
      ...prev,
      [order.order_id]: {
        ...order,
        items: updatedItems,
        total_amount: newTotal,
      },
    }));

    setAddMenuItemOrder(null);
  };

  const confirmDeleteOrder = async (orderId) => {
    if (isDeleting) return;

    setIsDeleting(true);

    try {
      const response = await apiClient.delete(
        `/owner/restaurant/${restaurantId}/delete-order/${orderId}`
      );
      if (response.status === 200) {
        sessionStorage.setItem("orderDeleted", "true");
        fetchOrders();
      }
    } catch (error) {
      console.error("Error deleting order:", error);
    } finally {
      setIsDeleting(false);
      setConfirmDeleteOrderId(null);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchOrders();
    }
  }, [currentPage, perPage]);

  // pagination controls component
  const PaginationControls = () => (
    <div className="flex justify-center items-center mt-8 gap-4">
      <button
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
        className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
          currentPage === 1
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-lg hover:shadow-xl"
        }`}
      >
        Previous
      </button>
      <span className="text-gray-700 font-semibold">
        Page {currentPage} of {totalPages}
      </span>
      <button
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
        className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
          currentPage === totalPages
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-lg hover:shadow-xl"
        }`}
      >
        Next
      </button>
    </div>
  );

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-4 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-2xl shadow-xl p-2 mb-4 text-center text-white">
          <h3 className="text-4xl font-bold mb-4">Order Management</h3>
          <p className="text-purple-100 text-lg">
            Manage and process customer orders in real-time
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mb-4"></div>
            <p className="text-gray-600 text-lg font-semibold">Loading orders...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-600 p-6 rounded-2xl text-center shadow-lg">
            {error}
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <p className="text-gray-600 text-xl font-semibold">No Orders Present</p>
            <p className="text-gray-500 mt-2">New orders will appear here when customers place them</p>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {orders.map((order) => (
                <div
                  key={order.order_id}
                  className="bg-white rounded-2xl shadow-xl p-6 w-full transform hover:scale-105 transition-all duration-300"
                >
                  {/* Order Header */}
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-4 border-b border-gray-200 gap-4">
                    <div className="space-y-2">
                      <p className="text-xl font-bold text-gray-800">
                        {order.customer_name}
                      </p>
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                        {order.phone_number}
                      </p>
                    </div>
                    <div className="text-left md:text-right space-y-2">
                      <p className="text-sm text-gray-600 bg-blue-50 p-2 rounded-lg">
                        Table: {order.table_number || "Pick Up"}
                      </p>
                      <p className="text-2xl font-bold text-purple-600">
                        ${order.total_amount.toFixed(2)}
                      </p>
                      <p className="text-sm font-semibold">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm
                      ${
                        order.status === "Completed"
                          ? "bg-green-100 text-green-800"
                          : order.status === "Cancelled"
                          ? "bg-red-100 text-red-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                        >
                          {order.status}
                        </span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Order #: {order.order_id}
                      </p>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-semibold text-gray-700">
                        Order Items
                      </h4>
                      <p className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                        {order.items.length} item{order.items.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div
                      className="space-y-3 overflow-y-auto"
                      style={{ maxHeight: "200px" }}
                    >
                      {order.items
                        .slice(
                          0,
                          expandedOrders[order.order_id]
                            ? order.items.length
                            : 3
                        )
                        .map((item) => (
                          <div
                            key={item.menu_item_id}
                            className="flex flex-col md:flex-row items-start md:items-center justify-between p-4 bg-gray-50 rounded-xl gap-3"
                          >
                            <span className="text-lg font-semibold text-gray-800 truncate flex-1">
                              {item.product_name}
                            </span>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-3 bg-white p-2 rounded-lg">
                                <button
                                  onClick={() =>
                                    handleDecrementQuantity(
                                      order.order_id,
                                      item
                                    )
                                  }
                                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center font-bold transition-all duration-200"
                                >
                                  -
                                </button>
                                <span className="w-8 text-center font-semibold">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    handleIncrementQuantity(
                                      order.order_id,
                                      item
                                    )
                                  }
                                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center font-bold transition-all duration-200"
                                >
                                  +
                                </button>
                              </div>
                              <span className="text-lg font-semibold text-gray-700 min-w-20 text-right">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                              <button
                                onClick={() =>
                                  handleDeleteOrderItem(
                                    order.order_id,
                                    item.menu_item_id
                                  )
                                }
                                className="text-red-500 hover:text-red-700 font-semibold transition-colors duration-200"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      {order.items.length > 3 && (
                        <button
                          onClick={() => toggleOrderItems(order.order_id)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-200"
                        >
                          {expandedOrders[order.order_id]
                            ? "Show Less"
                            : `Show All ${order.items.length} Items`}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Add Items Section */}
                  {addMenuItemOrder &&
                    addMenuItemOrder.order_id === order.order_id && (
                      <div className="mt-6 p-6 bg-gray-50 rounded-2xl border border-gray-200">
                        <div className="flex flex-col md:flex-row gap-4 mb-4">
                          <input
                            type="text"
                            placeholder="Search menu items..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="flex-1 p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                          />
                          <button
                            onClick={handleGoBack}
                            className="px-6 py-4 bg-gray-500 text-white rounded-xl font-semibold hover:bg-gray-600 transition-all duration-200"
                          >
                            Back
                          </button>
                        </div>

                        <div className="space-y-3">
                          {menuItems
                            .filter((item) =>
                              item.item_name
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase())
                            )
                            .map((item) => (
                              <div
                                key={item.menu_id}
                                className="flex justify-between items-center p-4 bg-white rounded-xl hover:bg-gray-100 transition-all duration-200"
                              >
                                <span className="font-semibold text-gray-800">
                                  {item.item_name}
                                </span>
                                <div className="flex items-center gap-4">
                                  <span className="font-semibold text-green-600">
                                    ${item.price.toFixed(2)}
                                  </span>
                                  <button
                                    onClick={() => addMenuItemToOrder(item)}
                                    className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {/* Action Buttons */}
                  <div className="mt-6 flex flex-col md:flex-row gap-3">
                    {order.status === "Pending" && (
                      <button
                        onClick={() => handleApproveOrder(order.order_id)}
                        disabled={isUpdating[order.order_id]}
                        className={`flex-1 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
                          isUpdating[order.order_id]
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl"
                        }`}
                      >
                        {isUpdating[order.order_id]
                          ? "Processing..."
                          : modifiedOrders[order.order_id]
                          ? "Update Order"
                          : "Approve Order"}
                      </button>
                    )}

                    <button
                      onClick={() => getMenuItems(order)}
                      className="flex-1 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Add Items
                    </button>

                    <button
                      onClick={() => handlePrintTicket(order)}
                      className="flex-1 py-4 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-xl font-semibold hover:from-gray-800 hover:to-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Print Ticket
                    </button>

                    {confirmDeleteOrderId === order.order_id ? (
                      <div className="flex flex-col md:flex-row gap-3 w-full">
                        <button
                          onClick={() => confirmDeleteOrder(order.order_id)}
                          disabled={isDeleting}
                          className={`flex-1 py-4 rounded-xl font-semibold text-lg transition-all duration-200 ${
                            isDeleting
                              ? "bg-red-400 cursor-not-allowed"
                              : "bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white shadow-lg hover:shadow-xl"
                          }`}
                        >
                          {isDeleting ? "Deleting..." : "Confirm Delete"}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteOrderId(null)}
                          className="flex-1 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl font-semibold hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleDeleteOrder(order.order_id)}
                        className="flex-1 py-4 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        Delete Order
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <PaginationControls />
          </>
        )}
      </div>
      <OrderTicket order={printingOrder} />
    </div>
  );
}

export default OrderManagement;